import React from 'react';
import { 
  Upload, Camera, Download, Trash2, AlertCircle, Loader, X, Eye
} from 'lucide-react';

// ============================================================================
// FILE SIZE LIMIT (shared with ReservationForm so both use the same number)
// ============================================================================
export const MAX_DOCUMENT_SIZE_MB = 5;
export const MAX_DOCUMENT_SIZE_BYTES = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;

// Images are compressed before upload (max 1280px, JPEG 60%), so they almost
// always end up far below the limit. Only non-image files (PDFs) are checked here.
const isFileSizeAllowed = (file) => {
  if (!file) return false;
  if (file.type.startsWith('image/')) return true;

  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    alert(
      `"${file.name}" is ${sizeMB} MB.\n\n` +
      `Maximum size for PDF files is ${MAX_DOCUMENT_SIZE_MB} MB.\n` +
      `Tip: upload a photo of the document instead - photos are compressed automatically.`
    );
    return false;
  }
  return true;
};

const DocumentRequirementsSection = ({
  reservationSettings,
  documentUploads,
  setDocumentUploads,
  documentValidation,
  setDocumentValidation,
  uploadProgress,
  capturedImages,
  setCapturedImages,
  cameraStream,
  setCameraStream,
  activeCameraDoc,
  setActiveCameraDoc,
  cameraSupported,
  uploadingDocuments,
  storageAvailable,
  editIndex,
  documentChanges,
  setDocumentChanges,
  originalDocuments
}) => {
  
  const getEnabledDocuments = () => {
    return (reservationSettings.documentRequirements || []).filter(doc => doc && doc.enabled === true);
  };

  const getRequiredDocuments = () => {
    const documents = getEnabledDocuments();
    return documents.filter(doc => doc.required === true);
  };

  const handleDocumentChange = (documentId, file) => {
    if (file) {
      // Reject oversized PDFs immediately, not at save time
      if (!isFileSizeAllowed(file)) return;

      setDocumentUploads(prev => ({
        ...prev,
        [documentId]: {
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          isNew: true,
          isExisting: false
        }
      }));
      
      setDocumentValidation(prev => ({
        ...prev,
        [documentId]: { isValid: true, message: '' }
      }));
    } else {
      const updatedUploads = { ...documentUploads };
      delete updatedUploads[documentId];
      setDocumentUploads(updatedUploads);
    }
    setDocumentChanges(true);
  };

  const handleReplaceDocument = (documentId, file) => {
    // Same check when replacing an existing document
    if (!isFileSizeAllowed(file)) return;

    setDocumentUploads(prev => ({
      ...prev,
      [documentId]: {
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        isNew: true,
        isExisting: false,
        replacing: true
      }
    }));
    setDocumentChanges(true);
  };

  const startCamera = async (documentId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      setActiveCameraDoc(documentId);
    } catch (error) {
      console.error('Error starting camera:', error);
      alert('Unable to access camera. Please ensure you have granted camera permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setActiveCameraDoc(null);
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera-video');
    if (video && activeCameraDoc) {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImages(prev => ({
        ...prev,
        [activeCameraDoc]: imageData
      }));
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `captured-${activeCameraDoc}-${Date.now()}.jpg`, { type: 'image/jpeg' });
        handleDocumentChange(activeCameraDoc, file);
      }, 'image/jpeg', 0.8);
      
      stopCamera();
    }
  };

  const enabledDocs = getEnabledDocuments();
  if (enabledDocs.length === 0) return null;

  const requiredDocs = getRequiredDocuments();
  
  // ✅ CHANGED: one flat list sorted by category (instead of one block per category),
  // so all documents can share a single 2-column grid
  const sortedDocuments = [...enabledDocs].sort((a, b) =>
    (a.category || 'Other').localeCompare(b.category || 'Other')
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
            <Upload size={16} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-medium">Document Requirements</h3>
          </div>
        </div>
        {editIndex !== null && (
          <div className={`px-2 py-1 rounded-full text-xs ${
            documentChanges 
              ? 'bg-orange-900/30 text-orange-400 border border-orange-700' 
              : 'bg-green-900/30 text-green-400 border border-green-700'
          }`}>
            {documentChanges ? 'Changes' : 'No Changes'}
          </div>
        )}
      </div>

      {!storageAvailable && (
        <div className="mb-4 bg-red-900/30 border border-red-700 rounded-lg p-3">
          <div className="flex items-center text-red-400 text-sm">
            <AlertCircle className="mr-2" size={16} />
            <span>Firebase Storage is not available. Document uploads will fail.</span>
          </div>
        </div>
      )}

      <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-4">
        {/* ✅ CHANGED: one grid, 2 columns per row on medium+ screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sortedDocuments.map(document => {
                  const docUpload = documentUploads[document.id];
                  
                  const hasExistingDoc = docUpload && docUpload.isExisting === true;
                  const hasNewDoc = docUpload && docUpload.isNew === true;
                  
                  const validation = documentValidation[document.id];
                  const hasError = validation && !validation.isValid;
                  const uploadProgressInfo = uploadProgress[document.id];

                  return (
                    <div
                      key={document.id}
                      className={`rounded-lg border p-3 transition-colors ${
                        hasError
                          ? 'border-red-500 bg-red-900/20'
                          : (hasExistingDoc || hasNewDoc)
                          ? 'border-green-500 bg-green-900/20'
                          : 'border-gray-600 bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white text-sm">{document.name}</span>
                            {document.required && (
                              <span className="text-xs bg-red-900/30 text-red-400 px-2 py-0.5 rounded-full border border-red-700">
                                Required
                              </span>
                            )}
                            {hasExistingDoc && !hasNewDoc && (
                              <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full border border-green-700">
                                ✓ Uploaded
                              </span>
                            )}
                          </div>
                          {/* ✅ CHANGED: category shown on the card instead of as a separate heading */}
                          <div className="text-xs text-purple-300/70 mt-0.5">{document.category || 'Other'}</div>
                          {document.description && (
                            <div className="text-xs text-gray-400 mt-1">{document.description}</div>
                          )}
                        </div>
                      </div>

                      {/* EXISTING DOCUMENT DISPLAY */}
                      {hasExistingDoc && !hasNewDoc && (
                        <div className="mb-3 bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4 text-blue-400" />
                              <div>
                                <div className="text-sm font-medium text-blue-300">Currently Uploaded</div>
                                <div className="text-xs text-gray-400">
                                  {docUpload.name} • {(docUpload.size / 1024).toFixed(1)} KB
                                </div>
                                {docUpload.uploadedAt && (
                                  <div className="text-xs text-gray-500">
                                    Uploaded: {new Date(docUpload.uploadedAt).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                            {docUpload.downloadURL && docUpload.downloadURL !== '' && (
                              <a
                                href={docUpload.downloadURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download size={12} />
                                <span>View</span>
                              </a>
                            )}
                          </div>
                          
                          {docUpload.downloadURL && docUpload.type && docUpload.type.startsWith('image/') && (
                            <div className="mt-2">
                              <img 
                                src={docUpload.downloadURL} 
                                alt={document.name}
                                className="w-full h-32 object-cover rounded border border-blue-500/30"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          {/* Replace Document Button */}
                          <div className="mt-2">
                            <label className="block">
                              <input
                                type="file"
                                accept={document.acceptedFormats?.join(',') || 'image/*,.pdf'}
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleReplaceDocument(document.id, e.target.files[0]);
                                  }
                                  // Reset so choosing the same file again still triggers onChange
                                  e.target.value = '';
                                }}
                                className="hidden"
                                disabled={uploadingDocuments}
                              />
                              <div className="flex items-center justify-center gap-2 p-2 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded cursor-pointer transition-colors">
                                <Upload size={12} />
                                <span>Replace Document</span>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* NEW DOCUMENT SELECTED */}
                      {hasNewDoc && (
                        <div className="mb-3 bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Upload className="w-4 h-4 text-green-400" />
                              <div>
                                <div className="text-sm font-medium text-green-300">
                                  {docUpload.replacing ? 'Replacing with:' : 'Ready to Upload:'}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {docUpload.name} • {(docUpload.size / 1024).toFixed(1)} KB
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (docUpload.replacing && originalDocuments[document.id]) {
                                  setDocumentUploads(prev => ({
                                    ...prev,
                                    [document.id]: originalDocuments[document.id]
                                  }));
                                } else {
                                  setDocumentUploads(prev => {
                                    const updated = { ...prev };
                                    delete updated[document.id];
                                    return updated;
                                  });
                                }
                                setCapturedImages(prev => {
                                  const updated = { ...prev };
                                  delete updated[document.id];
                                  return updated;
                                });
                              }}
                              className="text-red-400 hover:text-red-300"
                              disabled={uploadingDocuments}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* UPLOAD PROGRESS */}
                      {uploadProgressInfo && (
                        <div className="mb-2 bg-gray-800 rounded-lg p-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-300">
                              {uploadProgressInfo.status === 'uploading' && '⬆️ Uploading...'}
                              {uploadProgressInfo.status === 'retrying' && '🔄 Retrying...'}
                              {uploadProgressInfo.status === 'complete' && '✅ Complete'}
                              {uploadProgressInfo.status === 'error' && '❌ Failed'}
                              {uploadProgressInfo.attempt && ` (Attempt ${uploadProgressInfo.attempt})`}
                            </span>
                            <span className="text-gray-400">{uploadProgressInfo.size || ''}</span>
                          </div>
                          {uploadProgressInfo.error && (
                            <div className="text-red-400 text-xs mt-1">{uploadProgressInfo.error}</div>
                          )}
                        </div>
                      )}

                      {/* CAPTURED IMAGE PREVIEW */}
                      {capturedImages[document.id] && (
                        <div className="mb-2">
                          <img 
                            src={capturedImages[document.id]} 
                            alt="Captured" 
                            className="w-full h-32 object-cover rounded border border-gray-600"
                          />
                        </div>
                      )}

                      {/* UPLOAD ZONE (only show if no existing doc, or if replacing) */}
                      {(!hasExistingDoc || hasNewDoc) && (
                        <div className="flex gap-2">
                          <label className="flex-1">
                            <input
                              type="file"
                              accept={document.acceptedFormats?.join(',') || 'image/*,.pdf'}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleDocumentChange(document.id, e.target.files[0]);
                                }
                                // Reset so choosing the same file again still triggers onChange
                                e.target.value = '';
                              }}
                              className="hidden"
                              disabled={uploadingDocuments}
                            />
                            <div className={`flex items-center justify-center gap-2 p-2 rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
                              hasNewDoc
                                ? 'border-green-500 bg-green-900/20 hover:bg-green-900/30'
                                : 'border-gray-500 bg-gray-700/50 hover:bg-gray-700'
                            } ${uploadingDocuments ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <Upload size={16} className={hasNewDoc ? 'text-green-400' : 'text-gray-400'} />
                              <span className={`text-xs ${hasNewDoc ? 'text-green-400' : 'text-gray-400'}`}>
                                {hasNewDoc ? 'Change File' : 'Upload'}
                              </span>
                            </div>
                          </label>

                          {cameraSupported && (
                            <button
                              type="button"
                              onClick={() => startCamera(document.id)}
                              className="flex items-center justify-center gap-2 p-2 rounded-lg border-2 border-dashed border-gray-500 bg-gray-700/50 hover:bg-gray-700 transition-colors"
                              disabled={uploadingDocuments || activeCameraDoc !== null}
                            >
                              <Camera size={16} className="text-gray-400" />
                              <span className="text-xs text-gray-400">Camera</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* VALIDATION ERROR */}
                      {hasError && validation.message && (
                        <div className="text-red-400 text-xs mt-2 flex items-center gap-1">
                          <AlertCircle size={12} />
                          <span>{validation.message}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
        </div>

        {/* SUMMARY */}
        {Object.keys(documentUploads).length > 0 && (
          <div className="mt-4 bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-300">Documents:</span>
              <span className="text-purple-400 font-bold">
                {Object.keys(documentUploads).length} / {enabledDocs.length} attached
              </span>
            </div>
            <div className="text-xs text-purple-300">
              {requiredDocs.length} required, {Object.keys(documentUploads).length >= requiredDocs.length ? 'all provided' : `${requiredDocs.length - Object.keys(documentUploads).length} missing`}
            </div>
            {editIndex !== null && documentChanges && (
              <div className="mt-2 text-xs text-orange-400 bg-orange-900/20 px-2 py-1 rounded border border-orange-700">
                Changes will be saved when you update the reservation
              </div>
            )}
          </div>
        )}

        {uploadingDocuments && (
          <div className="mt-4 bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-blue-400">
              <Loader className="animate-spin" size={16} />
              <span className="text-sm font-medium">Uploading documents to Firebase Storage...</span>
            </div>
          </div>
        )}
      </div>

      {/* CAMERA MODAL */}
      {activeCameraDoc !== null && (
        <div className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-4 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium">Capture Document Photo</h4>
              <button onClick={stopCamera} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <video
              id="camera-video"
              autoPlay
              playsInline
              ref={(video) => {
                if (video && cameraStream) {
                  video.srcObject = cameraStream;
                }
              }}
              className="w-full rounded-lg bg-black"
            />
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={capturePhoto}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <Camera size={16} />
                Capture Photo
              </button>
              <button
                type="button"
                onClick={stopCamera}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentRequirementsSection;